import React, { useEffect, useRef, useState } from "react";
import { Button, Form } from "react-bootstrap";
import HighlightColorSelector from "./HighlightColorSelector";
import { Annotation } from "../utils/annotator/annotationsHelper";
import {
  readMostRecentHighlightColor,
  resolveHighlightColor,
} from "../utils/annotator/highlightColors";
import "./NoteModal.scss";
import PreviewQuote from "./PreviewQuote";

type NoteFormMode = "create" | "edit";

interface NoteFormProps {
  /** Which UI variant to render. */
  mode: NoteFormMode;
  /** Annotation being edited (omitted for "create"). */
  annotation?: Annotation;
  /** Text the annotation refers to; shown as a quoted preview at the top of the modal. */
  annotatedText?: string;
  /** Invoked when the user saves a new note ("create"). Receives trimmed note text (or `null` if blank) and the chosen highlight color. */
  onSave?: (note: string | null, colorValue: string) => void;
  /** Invoked when the user saves an edited note ("edit"). Receives trimmed note text (or `null` if blank) and the chosen highlight color. */
  onEdit?: (note: string | null, colorValue: string) => void;
  /** Invoked when the user clicks Delete. Only shown in "edit" mode, and only when provided; the caller owns the confirmation step. */
  onDeleteRequest?: () => void;
  /** Invoked when the user clicks the "All Notes" button. Only shown in "edit" mode, and only when provided. */
  onViewAll?: () => void;
  /** Invoked when the user cancels. */
  onCancel?: () => void;
}

/**
 * The create / edit body for all annotation types
 */
function NoteForm({
  mode,
  annotation,
  annotatedText,
  onSave,
  onEdit,
  onDeleteRequest,
  onViewAll,
  onCancel,
}: NoteFormProps) {
  const [note, setNote] = useState(annotation?.note ?? "");
  const [selectedColor, setSelectedColor] = useState(() =>
    mode === "edit"
      ? resolveHighlightColor(annotation?.color)
      : readMostRecentHighlightColor(),
  );
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  function handleSave() {
    onSave?.(note.trim() || null, selectedColor);
  }

  function handleEdit() {
    onEdit?.(note.trim() || null, selectedColor);
  }

  return (
    <>
      <PreviewQuote text={annotatedText || annotation?.text} />
      <Form.Control
        as="textarea"
        aria-label={mode === "create" ? "Add a note" : "Edit note"}
        id="note-modal-textarea"
        ref={textareaRef}
        rows={4}
        placeholder="Write your note..."
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />

      {mode === "create" && (
        <div className="noteModal__hint bg-light rounded p-3 small mb-3">
          Leave the note blank to save just the highlight.
        </div>
      )}

      {mode === "edit" && (
        <HighlightColorSelector
          selectedColor={selectedColor}
          onColorChange={setSelectedColor}
          className="mt-3"
        />
      )}

      <hr aria-hidden="true" className="w-100" />

      <div
        className={
          mode === "create"
            ? "d-flex justify-content-between align-items-center"
            : "d-flex justify-content-between"
        }
      >
        {mode === "create" ? (
          <HighlightColorSelector
            selectedColor={selectedColor}
            onColorChange={setSelectedColor}
          />
        ) : (
          <div className="d-flex gap-1">
            {onViewAll && (
              <Button variant="outline-secondary" onClick={onViewAll}>
                All Notes
              </Button>
            )}
            {onDeleteRequest && (
              <Button
                variant="outline-danger"
                className="ml-1"
                onClick={onDeleteRequest}
              >
                Delete
              </Button>
            )}
          </div>
        )}

        <div className={mode === "create" ? "d-flex" : "d-flex gap-1"}>
          <Button
            variant="outline-secondary"
            className={mode === "create" ? "fw-bold" : "ml-1"}
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            className={
              mode === "create" ? "noteModal__btn--save ml-1 fw-bold" : "ml-1"
            }
            onClick={mode === "create" ? handleSave : handleEdit}
          >
            Save
          </Button>
        </div>
      </div>
    </>
  );
}

export default NoteForm;
